// Pure helpers behind payout requests and direct payouts (admin side),
// extracted from useGetPaid with the same rules.
import type { BackendPayoutRequest, PaymentNetwork, UserForPayout } from "@/lib/types/payments";
import { PaymentMethod } from "@/lib/types/payments";
import { resolveRequestType } from "../helpers";

/** Requests of all statuses, one per id (the later list wins), newest first. */
export function mergeRequestsNewestFirst(lists: BackendPayoutRequest[][]): BackendPayoutRequest[] {
  const merged = new Map<string, BackendPayoutRequest>();
  lists.flat().forEach((item) => merged.set(item.id, item));
  return Array.from(merged.values()).sort(
    (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
  );
}

export const requestsOfType = (requests: BackendPayoutRequest[], type: "USER" | "MODERATOR") =>
  requests.filter((request) => resolveRequestType(request) === type);

export const pendingRequestsOfType = (requests: BackendPayoutRequest[], type: "USER" | "MODERATOR") =>
  requestsOfType(requests, type).filter((request) => request.status === "PENDING");

export const totalAmount = (requests: { amount: number }[]) => requests.reduce((sum, request) => sum + request.amount, 0);

/** What a moderator sees in the pending list: contributor requests only. */
export const moderatorVisibleRequests = (items: BackendPayoutRequest[]) =>
  items.filter((r) => r.requestType === "USER" || !r.requestType);

/**
 * Contributors with validated tests, one row per user. Only users whose
 * summed test score reaches 70 are kept; the amount is a first estimate the
 * server's accumulated amount replaces when it answers.
 */
export function aggregateEligibleUsers(items: any[], contributorAmount: number): UserForPayout[] {
  const userMap = new Map<string, any>();
  items.forEach((item) => {
    const userId = item.userId || item.user?.id;
    const userName = item.user?.name || item.userName || userId;
    const userEmail = item.user?.email || "";
    const userPhone = item.user?.phoneNumber || "";
    const scoreRaw = item.earnedPoints ?? item.totalScore ?? item.score ?? 0;
    const score = typeof scoreRaw === "string" ? parseFloat(scoreRaw) : scoreRaw;
    if (!userMap.has(userId)) {
      userMap.set(userId, {
        id: userId,
        username: userName,
        email: userEmail,
        phoneNumber: userPhone,
        validatedTests: 0,
        totalScore: 0,
        hasPaymentInfo: !!userPhone,
      });
    }
    const entry = userMap.get(userId);
    entry.validatedTests += 1;
    entry.totalScore += score;
  });

  return Array.from(userMap.values())
    .filter((u) => u.totalScore >= 70)
    .map((u) => ({
      ...u,
      score: Math.round((u.totalScore / u.validatedTests) * 10) / 10,
      amount: Math.round(u.validatedTests * contributorAmount),
    }));
}

/** First name and last name for FeexPay; a single word is used twice. */
export function splitPayerName(name: unknown) {
  const nameParts = String(name || "").trim().split(/\s+/).filter(Boolean);
  const firstName = nameParts[0] || "Admin";
  const lastName = nameParts.slice(1).join(" ") || firstName;
  return { firstName, lastName };
}

/** Only Flooz and T-Money exist on FeexPay; other providers fall back to Flooz. */
export const PROVIDER_TO_NETWORK: Record<string, PaymentNetwork | undefined> = {
  [PaymentMethod.FLOOZ]: "FLOOZ",
  [PaymentMethod.TMONEY]: "TMONEY",
  [PaymentMethod.WAVE]: "FLOOZ",
  [PaymentMethod.BANK_TRANSFER]: "FLOOZ",
  [PaymentMethod.CASH]: "FLOOZ",
  [PaymentMethod.OTHER]: "FLOOZ",
  // Support for old values
  tmoney: "TMONEY",
  flooz: "FLOOZ",
  wave: "FLOOZ",
  bank: "FLOOZ",
  mtn: "FLOOZ",
  moov: "FLOOZ",
};

/** The prompt's answer as a network, or null when it is neither Flooz nor T-Money. */
export function parseNetworkInput(input: string | null): PaymentNetwork | null {
  const network = String(input || "").toUpperCase() as PaymentNetwork;
  return network === "FLOOZ" || network === "TMONEY" ? network : null;
}

/** The server's message, then the error's, then the fallback. */
export const apiErrorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.message || error?.message || fallback;
